import { useRef, useState, type Ref } from 'react';
import { PencilSimpleIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ResponsiveDrawer } from '@/components/ui/responsive-drawer';
import { Skeleton } from '@/components/ui/skeleton';
import { getCategoryMeta, type CategoryIndex } from '@/components/transactions/screen/categoryIndex';
import { useCategories } from '@/hooks/queries/useCategories';
import { useSpaceBudget } from '@/hooks/queries/useSpaceBudget';
import { useCategoryIndex } from '@/hooks/useCategoryIndex';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { useSpaceBudgetEditor } from '@/hooks/useSpaceBudgetEditor';
import { useSpaceActionsHidden } from '@/hooks/useSpacePartnerPreview';
import { budgetCategories, isBudget } from '@/lib/spaces/budget';
import { spaceBudgetTaxonomy, spaceCategorySpending, type SpaceCategorySpend } from '@/lib/spaces/categories';
import { budgetProgress, homeSheetFocusTarget, readFailed } from '@/lib/spaces/home';
import { settlementPeriodLabel } from '@/lib/spaces/settlement';
import type { SpaceBudget, SpaceDetail } from '@/types/spaces';
import { SettlementFailure } from './SettlementParts';
import { SpaceBudgetEditor, type BudgetTaxonomy } from './SpaceBudgetEditor';
import { SpaceBudgetBar, SpaceCategorySpendRow } from './SpaceBudgetParts';

const EYEBROW = 'font-mono text-2xs font-semibold uppercase tracking-widest text-text-secondary';

// The API replaces the budget as a whole, so an editor edits all of it from here rather than one line at a time.
export function SpaceBudgetSheet({ space, period, close }: Readonly<{ space: SpaceDetail; period: string; close: () => void }>) {
  const budget = useSpaceBudget(space.id, period);
  const editor = useSpaceBudgetEditor(space.id, period);
  const tree = useCategories();
  // The budget the editor opened on. A failed save clears the cached budget to read it again, and the editor must
  // not unmount with it: the draft is the person's, and the API's reason is about that draft.
  const [editing, setEditing] = useState<SpaceBudget | null>(null);
  const editButton = useRef<HTMLButtonElement>(null);
  const [returnTarget] = useState(() => document.activeElement);
  // The editor offers what the API accepts a line on; labels for anything else still come from the full taxonomy.
  const taxonomy: BudgetTaxonomy = { categories: budgetCategories(spaceBudgetTaxonomy(tree.data)), ready: Boolean(tree.data), failed: Boolean(tree.error) && !tree.data, retry: tree.mutate };
  const failed = readFailed(budget, isBudget);
  const data = failed ? undefined : budget.data;
  const stopEditing = () => setEditing(null);
  const focusEditButton = () => editButton.current;
  if (editing) return <SpaceBudgetEditor space={space} editor={editor} budget={editing} taxonomy={taxonomy} close={stopEditing} returnFocus={focusEditButton} />;
  const startEditing = () => { if (!data) return; editor.clearError(); setEditing(data); };
  const handleOpenChange = (open: boolean) => { if (!open) close(); };
  // No Radix Trigger opened this sheet, so focus goes back by hand.
  const handleRestoreFocus = (event: Event) => {
    event.preventDefault();
    homeSheetFocusTarget(returnTarget)?.focus();
  };
  return <ResponsiveDrawer open onOpenChange={handleOpenChange}>
    <DrawerContent onCloseAutoFocus={handleRestoreFocus} className="overflow-clip bg-surface p-0 data-[vaul-drawer-direction=right]:w-full data-[vaul-drawer-direction=right]:sm:max-w-lg">
      <DrawerHeader className="shrink-0 border-b border-border-subtle px-5 py-4 text-left group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left">
        <div className="flex items-center justify-between gap-3"><DrawerTitle>Orçamento</DrawerTitle><Button variant="ghost" size="icon" aria-label="Fechar orçamento" onClick={close}><XIcon aria-hidden className="size-4" /></Button></div>
        <DrawerDescription className="break-words text-text-secondary">{space.name} · {period === 'trip' ? 'toda a viagem' : settlementPeriodLabel(period)}</DrawerDescription>
      </DrawerHeader>
      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-4" data-vaul-no-drag>
        <BudgetBody space={space} budget={budget} failed={failed} tree={tree} />
      </div>
      <EditBudget ref={editButton} budget={data} busy={editor.busy} onEdit={startEditing} />
    </DrawerContent>
  </ResponsiveDrawer>;
}

function BudgetBody({ space, budget, failed, tree }: Readonly<{
  space: SpaceDetail; budget: ReturnType<typeof useSpaceBudget>; failed: boolean; tree: ReturnType<typeof useCategories>;
}>) {
  const categoryIndex = useCategoryIndex();
  if (failed) return <SettlementFailure message="Não foi possível carregar o orçamento do espaço." retry={budget.mutate} />;
  if (!budget.data) return <div aria-busy="true" aria-label="Carregando orçamento" className="space-y-3"><Skeleton className="h-24 rounded-xl" /><Skeleton className="h-10 rounded-xl" /><Skeleton className="h-10 rounded-xl" /></div>;
  // Without the taxonomy the rows still read, by their ids; the retry is for the names.
  const rows = spaceCategorySpending(budget.data, tree.data);
  return <>
    <BudgetTotal space={space} budget={budget.data} />
    <section aria-label="Por categoria" className="space-y-3">
      <h3 className={EYEBROW}>Por categoria</h3>
      <TaxonomyFailure visible={Boolean(tree.error) && !tree.data} retry={tree.mutate} />
      <CategoryList rows={rows} categoryIndex={categoryIndex} />
    </section>
  </>;
}

function BudgetTotal({ space, budget }: Readonly<{ space: SpaceDetail; budget: SpaceBudget }>) {
  const format = useFormatCurrency();
  const progress = budgetProgress(budget.spentCents, budget.limitCents);
  const spent = format(budget.spentCents / 100, { noCents: true });
  return <section aria-label="Limite" className="space-y-2 rounded-xl bg-surface-sunken p-4">
    <h3 className={EYEBROW}>{space.kind === 'trip' ? 'Limite da viagem' : 'Limite do mês'}</h3>
    <p className="font-mono text-2xl font-semibold text-text-primary tabular-nums">{budget.limitCents ? format(budget.limitCents / 100, { noCents: true }) : 'Sem limite'}</p>
    <SpaceBudgetBar label="Orçamento usado" progress={progress} className="h-2" />
    <p className={`font-mono text-xs tabular-nums ${progress?.over ? 'text-error-text' : 'text-text-secondary'}`}>gasto {spent}{progress ? ` · ${progress.pct.toLocaleString('pt-BR')}%` : ''}</p>
  </section>;
}

function CategoryList({ rows, categoryIndex }: Readonly<{ rows: SpaceCategorySpend[]; categoryIndex: CategoryIndex }>) {
  if (!rows.length) return <p className="text-sm text-text-secondary">Nada entrou no espaço neste período.</p>;
  const renderChild = (child: SpaceCategorySpend) => <li key={child.categoryId}><SpaceCategorySpendRow row={child} meta={getCategoryMeta(categoryIndex, child.categoryId)} nested /></li>;
  const renderRow = (row: SpaceCategorySpend) => <li key={row.categoryId} className="space-y-2">
    <SpaceCategorySpendRow row={row} meta={getCategoryMeta(categoryIndex, row.categoryId)} />
    {row.children.length ? <ul aria-label={`Limites dentro de ${row.label}`} className="space-y-2">{row.children.map(renderChild)}</ul> : null}
  </li>;
  return <ul aria-label="Categorias do espaço" className="space-y-4">{rows.map(renderRow)}</ul>;
}

function TaxonomyFailure({ visible, retry }: Readonly<{ visible: boolean; retry: () => unknown }>) {
  if (!visible) return null;
  return <SettlementFailure message="Não foi possível carregar os nomes das categorias compartilhadas." retry={retry} />;
}

// `canEdit` is the API's word (an editor of an open space); a partner preview puts the viewer's own actions away.
// While a save is in flight the editor stays shut: it would open on the budget that save is replacing.
function EditBudget({ ref, budget, busy, onEdit }: Readonly<{ ref: Ref<HTMLButtonElement>; budget: SpaceBudget | undefined; busy: boolean; onEdit: () => void }>) {
  const hidden = useSpaceActionsHidden();
  if (!budget?.canEdit || hidden) return null;
  return <div className="shrink-0 border-t border-border-subtle bg-surface px-5 py-4">
    <Button ref={ref} className="w-full" disabled={busy} onClick={onEdit}><PencilSimpleIcon aria-hidden weight="bold" className="size-4" />{budget.limitCents || budget.lines.length ? 'Editar orçamento' : 'Definir orçamento'}</Button>
  </div>;
}
